/**
 * The Product page's pure logic: where it was opened from (back link and breadcrumb), its tabs,
 * the run-product status words, and the fix plan — what `POST /runs` a "Fix problems" starts.
 */
import type {
  ContentField,
  ContentProvider,
  GenerationRunCreate,
  GenerationRunRead,
} from '../types/content.types';
import { CONTENT_FIELDS, CONTENT_PROVIDERS } from '../types/content.types';
import type { FieldContent } from '../types/quality-scores.types';

import { catalogRunId, groupRunsIntoJobs, nextFixNumber, parseRunId } from './catalog-run-ids';
import { REDO_UNDERSTANDING_LABEL } from './flow-label';
import { GLOSSARY, RUN_PRODUCT_STATUS_GLOSSARY } from './glossary';
import type { ProductIssue } from './product-issues';
import { newQcRunId } from './qc-steps';
import { fieldLabel } from './quality-labels';
import type { StatusTone } from './run-status';
import { humanizeEnum } from './run-status';
import { runScores, runTitleLabel, runWrites } from './run-summary';

/** Which list opened the page — drives the breadcrumb and the back link. */
export const PRODUCT_PAGE_FROM = ['qc', 'catalog', 'job'] as const;
export type ProductPageFrom = (typeof PRODUCT_PAGE_FROM)[number];

/**
 * The page's tabs, Type & attributes first. Its URL value stays `understanding` so old links
 * keep working.
 */
export const PRODUCT_PAGE_TABS = ['understanding', 'issues', 'content', 'score'] as const;
export type ProductPageTab = (typeof PRODUCT_PAGE_TABS)[number];

/** A run that neither writes nor scores (e.g. Understand): its products have nothing to fix or score. */
export function isUnderstandingOnly(run: Pick<GenerationRunRead, 'flows'>): boolean {
  return !runWrites(run) && !runScores(run);
}

/** The tabs a product of this run shows: Understand-only runs have no Content or Score. */
export function productPageTabsFor(input: {
  understandingOnly: boolean;
  /** Run problems + QC findings — an Understand-only run's Issues tab shows only when there are some. */
  understandingIssues: number;
}): ProductPageTab[] {
  return input.understandingOnly
    ? ['understanding', ...(input.understandingIssues > 0 ? (['issues'] as const) : [])]
    : ['understanding', 'issues', 'content', 'score'];
}

/** The tab every product opens on: Type & attributes (URL value `understanding`), for every run. */
export function defaultProductPageTab(): ProductPageTab {
  return 'understanding';
}

/** Where a run's product page should say it came from, judged from the run id's family. */
export function productPageFrom(runId: string): ProductPageFrom {
  const kind = parseRunId(runId).kind;
  if (kind === 'qc') return 'qc';
  if (kind === 'other') return 'job';
  return 'catalog';
}

/** Tone per `RunProductStatus`; the words come from the glossary. */
const RUN_PRODUCT_STATUS_TONE: Record<string, StatusTone> = {
  ENRICHED: 'success',
  PARTIAL: 'warning',
  FAILED: 'error',
};

/** `RunProductStatus` → glossary words, meaning and tone for the header pill; unknown values are humanised. */
export function runProductStatusInfo(status: string): {
  label: string;
  tone: StatusTone;
  explanation: string;
} {
  const entry = RUN_PRODUCT_STATUS_GLOSSARY[status];
  return {
    label: entry?.label ?? humanizeEnum(status),
    tone: RUN_PRODUCT_STATUS_TONE[status] ?? 'muted',
    explanation: entry?.meaning ?? GLOSSARY.productStatus.meaning,
  };
}

/** Score field codes whose feed value counts as "the feed gives dimensions". */
const DIMENSION_FIELDS = ['PRODUCT_LENGTH', 'PRODUCT_WIDTH', 'PRODUCT_HEIGHT', 'SIZE'];

/** Feed attribute keys that name a dimension (merchants spell these many ways). */
const DIMENSION_KEY = /dimension|length|width|height|depth/i;

/**
 * Whether the feed carries any dimension — a scored dimension field with a feed value, or a
 * feed attribute named like one. Feeds the documented `description.states_dimensions` override.
 */
export function feedHasDimensions(
  content: readonly FieldContent[],
  feedAttributes: Record<string, string> | undefined,
): boolean {
  const scored = content.some(
    (c) => DIMENSION_FIELDS.includes(c.field) && (c.before ?? '').trim() !== '',
  );
  if (scored) return true;
  return Object.entries(feedAttributes ?? {}).some(
    ([key, value]) => DIMENSION_KEY.test(key) && value.trim() !== '',
  );
}

/** Whether an issue is fixed by a new run (written fields or re-extraction), not by the customer. */
export function isFixableIssue(issue: Pick<ProductIssue, 'source'>): boolean {
  return issue.source === 'instructions' || issue.source === 'attributes';
}

/** What a fix run does: rewrite fields, regenerate as before, write a skipped product, or re-extract. */
export type FixPlanKind = 'fix' | 'field' | 'regenerate' | 'write' | 'reextract';

/** One plan the sheet shows and the button sends. */
export interface FixPlan {
  kind: FixPlanKind;
  /** Plain title for the sheet ("Fix 3 problems"). */
  title: string;
  /** What will run, one line each. */
  steps: string[];
  /** Issues a run can't fix ("2 from your feed (GTIN, Brand) — …"), one line each. */
  notFixed?: string[];
  payload: GenerationRunCreate;
}

/** Narrows a score field code to a field `POST /runs` accepts; null when it isn't one. */
function asContentField(field: string): ContentField | null {
  return CONTENT_FIELDS.find((f) => f === field) ?? null;
}

/**
 * The `fields` to send for the affected written fields. Only codes `POST /runs` knows can be
 * named; if any affected field is outside that list, send none so the run writes them all.
 */
export function fixFields(fields: readonly string[]): ContentField[] | undefined {
  const unique = [...new Set(fields)];
  if (unique.length === 0) return undefined;
  const mapped = unique.map(asContentField);
  if (mapped.some((f) => f === null)) return undefined;
  return mapped.filter((f): f is ContentField => f !== null);
}

/** The channel the product was scored for, as `POST /runs` takes it; undefined when unknown. */
export function asProvider(platform: string): ContentProvider | undefined {
  return CONTENT_PROVIDERS.find((p) => p === platform);
}

/**
 * The run id a fix gets. QC: a fresh `-qc` id. Catalog job: `<job>-fix<n>`, the next free n
 * among the loaded runs (`useProductFixRun` probes it free on start). Else none: the server picks.
 */
export function fixRunId(sourceRunId: string, loadedRuns: readonly GenerationRunRead[]) {
  const parsed = parseRunId(sourceRunId);
  if (parsed.kind === 'qc') return newQcRunId();
  if (parsed.kind === 'other') return undefined;
  const job = groupRunsIntoJobs(loadedRuns).catalogJobs.find((j) => j.jobId === parsed.jobId);
  // The source run may itself be a fix the list hasn't loaded yet; never reuse its number.
  const next = Math.max(nextFixNumber(job ?? { fixes: [] }), (parsed.fixN ?? 0) + 1);
  return catalogRunId(parsed.jobId, 'fix', next);
}

/** Inputs every fix plan needs. */
export interface FixPlanInput {
  productId: string;
  sourceRun: GenerationRunRead;
  /** The channel from the product's score detail. */
  platform: string;
  /** Runs loaded so far — for the catalog job's next fix number. */
  loadedRuns: readonly GenerationRunRead[];
}

/** Assembles the `POST /runs` body: one product, previously generated included, the source run's flows and channel. */
function payloadFor(
  input: FixPlanInput,
  fields: ContentField[] | undefined,
  forceExtract: boolean,
): GenerationRunCreate {
  return productsFixPayload({
    sourceRun: input.sourceRun,
    loadedRuns: input.loadedRuns,
    platform: input.platform,
    productIds: [input.productId],
    fields,
    forceExtract,
  });
}

/**
 * A fix run over several products (e.g. the QC Summary's "Fix these N"): the products with
 * previously generated content included, the source run's flows and channel, `fields` only when
 * every one is a code `POST /runs` accepts, `forceExtract` to re-extract first. Run id as `fixRunId`.
 */
export function productsFixPayload(input: {
  sourceRun: GenerationRunRead;
  loadedRuns: readonly GenerationRunRead[];
  platform: string;
  productIds: readonly string[];
  fields: ContentField[] | undefined;
  forceExtract: boolean;
}): GenerationRunCreate {
  const runId = fixRunId(input.sourceRun.runId, input.loadedRuns);
  const platform = asProvider(input.platform);
  return {
    ...(runId !== undefined && { runId }),
    ...(input.fields && { fields: input.fields }),
    ...(input.sourceRun.flows.length > 0 && { flows: input.sourceRun.flows }),
    ...(platform && { platform }),
    ...(input.forceExtract && { forceExtract: true }),
    trigger: 'MANUAL',
    productFilter: { productIds: [...new Set(input.productIds)], includePreviouslyGenerated: true },
  };
}

/** Field names in words, joined ("Title, Description"). */
function fieldWords(fields: readonly string[]): string {
  return [...new Set(fields)].map(fieldLabel).join(', ');
}

/**
 * The header's primary plan: with fixable issues, rewrite their written fields (and re-extract
 * when any attributes issue exists); otherwise regenerate the product as the source run did.
 */
export function primaryFixPlan(input: FixPlanInput, issues: readonly ProductIssue[]): FixPlan {
  const fixable = issues.filter(isFixableIssue);
  if (fixable.length === 0) return regeneratePlan(input);
  const written = fixable.filter((i) => i.source === 'instructions').map((i) => i.field);
  const reextract = fixable.some((i) => i.source === 'attributes');
  // Re-extraction can change every written field, so it rewrites them all.
  const fields = reextract ? undefined : fixFields(written);
  const steps = [
    ...(reextract ? [REDO_UNDERSTANDING_LABEL] : []),
    written.length > 0 && fields
      ? `Rewrite ${fieldWords(written)} with the current instructions`
      : 'Rewrite every field the run writes with the current instructions',
    'Score it again so you can compare',
  ];
  return {
    kind: 'fix',
    title: `Fix ${fixable.length.toLocaleString()} ${fixable.length === 1 ? 'problem' : 'problems'}`,
    steps,
    notFixed: notFixedLines(issues),
    payload: payloadFor(input, fields, reextract),
  };
}

/** The issues a new run can't fix, in words: feed ones (with their fields) and rule ones. */
function notFixedLines(issues: readonly ProductIssue[]): string[] {
  const feed = issues.filter((i) => i.source === 'feed');
  const rules = issues.filter((i) => i.source === 'rules');
  return [
    ...(feed.length > 0
      ? [
          `${feed.length.toLocaleString()} from your feed (${fieldWords(feed.map((i) => i.field))}) — copy the note for the customer from the Problems table.`,
        ]
      : []),
    ...(rules.length > 0
      ? [`${rules.length.toLocaleString()} from a scoring rule — the rules are read-only here.`]
      : []),
  ];
}

/** Rewrite one written field — the Issues table's "Regenerate <field>". */
export function fieldFixPlan(input: FixPlanInput, field: string): FixPlan {
  const fields = fixFields([field]);
  return {
    kind: 'field',
    title: `Regenerate ${fieldLabel(field).toLowerCase()}`,
    steps: [
      fields
        ? `Rewrite ${fieldLabel(field)} with the current instructions`
        : `Rewrite every field the run writes (${fieldLabel(field)} can't be asked for alone)`,
      'Score it again so you can compare',
    ],
    payload: payloadFor(input, fields, false),
  };
}

/** Regenerate the product the way the source run did (same fields). */
export function regeneratePlan(input: FixPlanInput): FixPlan {
  const fields = fixFields(input.sourceRun.fields);
  return {
    kind: 'regenerate',
    title: 'Regenerate this product',
    steps: [
      fields
        ? `Write ${fieldWords(fields)} again with the current instructions`
        : 'Write every field the run writes again with the current instructions',
      'Score it again so you can compare',
    ],
    payload: payloadFor(input, fields, false),
  };
}

/**
 * Write a product the run skipped because its type wasn't chosen — "Write in a new run". The fix
 * sends no `enrichVerticals`, so every type (its own included) is written.
 */
export function writePlan(input: FixPlanInput, typeLabel: string): FixPlan {
  const fields = fixFields(input.sourceRun.fields);
  return {
    kind: 'write',
    title: 'Write in a new run',
    steps: [
      `Write ${fields ? fieldWords(fields) : 'every field the run writes'} with ${typeLabel} chosen and the current instructions`,
      'Score it so you can compare',
    ],
    payload: payloadFor(input, fields, false),
  };
}

/** Where a started fix run is opened: its QC check, its catalog job, or the job page. */
export type FixRunTarget =
  | { kind: 'qc'; runId: string }
  | { kind: 'catalog'; jobId: string }
  | { kind: 'job'; runId: string };

/** The page a fix run lives on, from its id's family. */
export function fixRunTarget(runId: string): FixRunTarget {
  const parsed = parseRunId(runId);
  if (parsed.kind === 'qc') return { kind: 'qc', runId };
  if (parsed.kind === 'other') return { kind: 'job', runId };
  return { kind: 'catalog', jobId: parsed.jobId };
}

/**
 * Work the product out again from scratch, then rewrite — "Redo attributes & rewrite". For a run
 * that neither writes nor scores (Understand) it only redoes detection and attributes.
 */
export function reextractPlan(input: FixPlanInput): FixPlan {
  if (isUnderstandingOnly(input.sourceRun)) {
    return {
      kind: 'reextract',
      title: REDO_UNDERSTANDING_LABEL,
      steps: ['Run Product group detection and Attribute extraction again for this product'],
      payload: payloadFor(input, undefined, true),
    };
  }
  const fields = fixFields(input.sourceRun.fields);
  return {
    kind: 'reextract',
    title: 'Redo attributes & rewrite',
    steps: [
      `${REDO_UNDERSTANDING_LABEL} (costs a new extraction)`,
      fields
        ? `Write ${fieldWords(fields)} again from the new attributes`
        : 'Write every field the run writes again from the new attributes',
      'Score it again so you can compare',
    ],
    payload: payloadFor(input, fields, true),
  };
}

/** The note a merchandiser sends the customer for a feed issue — plain text, ready to paste. */
export function customerNote(
  productLabel: string,
  issue: Pick<ProductIssue, 'field' | 'words' | 'measuredVsNeeded'>,
): string {
  const detail = issue.measuredVsNeeded ? ` (${issue.measuredVsNeeded})` : '';
  return `Product ${productLabel}: ${issue.words}${detail}. Please add ${fieldLabel(issue.field).toLowerCase()} to your product feed so we can use it.`;
}

/**
 * One note for every feed issue on the product — what the customer must add, ready to paste.
 * Empty when the feed is not to blame for anything.
 */
export function customerNoteAll(
  productLabel: string,
  issues: readonly Pick<ProductIssue, 'source' | 'field' | 'words' | 'measuredVsNeeded'>[],
): string {
  const feed = issues.filter((i) => i.source === 'feed');
  if (feed.length === 0) return '';
  const lines = feed.map(
    (i) =>
      `• ${fieldLabel(i.field)}: ${i.words}${i.measuredVsNeeded ? ` (${i.measuredVsNeeded})` : ''}`,
  );
  return [`Product ${productLabel}`, ...lines, 'Please add these to the product feed.'].join('\n');
}

/** The run's name in the breadcrumb: "QC check · Content scoring", "Catalog run · …" or the job's title. */
export function runBreadcrumbLabel(from: ProductPageFrom, run: GenerationRunRead): string {
  const what = runTitleLabel(run);
  if (from === 'qc') return `QC check · ${what}`;
  if (from === 'catalog') return `Catalog run · ${what}`;
  return what;
}
